// Lightweight client for server-side Places proxy

export type ServerPlace = {
  place_id: string;
  name: string;
  formatted_address: string;
  types?: string[];
  rating?: number;
  user_ratings_total?: number;
  geometry?: { lat: number; lng: number };
  business_status?: string;
};

// Simple in-memory cache and in-flight de-dup (per tab/process)
type CacheEntry<T> = { at: number; value: T };
const CACHE_TTL_MS = 30_000; // 30s cache for text search results
const DETAILS_TTL_MS = 5 * 60_000; // 5 min for details
const memCache = new Map<string, CacheEntry<any>>();
const inflight = new Map<string, Promise<any>>();

function getCached<T>(key: string, ttl: number): T | null {
  const it = memCache.get(key);
  if (!it) return null;
  if (Date.now() - it.at > ttl) { memCache.delete(key); return null; }
  return it.value as T;
}
function setCached<T>(key: string, value: T) { memCache.set(key, { at: Date.now(), value }); }

function getBase() {
  const raw = (process.env.REACT_APP_OEMBED_BASE || '').trim();
  // Strip accidental quotes from env (e.g., "" or 'https://...') and trailing slashes
  const noQuotes = raw.replace(/^[\'\"]+|[\'\"]+$/g, '');
  const base = noQuotes.replace(/\/+$/, '');
  return base;
}

export async function searchPlacesServer(query: string, opts?: { lat?: number; lng?: number; radius?: number; language?: string; region?: string; signal?: AbortSignal }) {
  if (!query?.trim()) return [] as ServerPlace[];
  const base = getBase();
  const origin = (typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
  const u = new URL(`${base}/places`, origin);
  u.searchParams.set('query', query);
  if (opts?.lat != null && opts?.lng != null) {
    u.searchParams.set('lat', String(opts.lat));
    u.searchParams.set('lng', String(opts.lng));
  }
  if (opts?.radius != null) u.searchParams.set('radius', String(opts.radius));
  if (opts?.language) u.searchParams.set('language', String(opts.language));
  if (opts?.region) u.searchParams.set('region', String(opts.region));

  // Build stable cache key (rounded coords to reduce key explosion)
  const key = JSON.stringify({
    mode: 'text',
    q: query.toLowerCase(),
    lat: opts?.lat != null ? Number((opts.lat as number).toFixed(3)) : undefined,
    lng: opts?.lng != null ? Number((opts.lng as number).toFixed(3)) : undefined,
    radius: opts?.radius,
    language: (opts?.language || '').toLowerCase(),
    region: (opts?.region || '').toLowerCase(),
  });
  const cached = getCached<ServerPlace[]>(key, CACHE_TTL_MS);
  if (cached) return cached;

  if (inflight.has(key)) {
    try { return await inflight.get(key)! as ServerPlace[]; } finally { /* keep inflight until resolved */ }
  }
  const p = (async () => {
    const res = await fetch(u.toString(), { signal: opts?.signal, credentials: 'omit' });
    if (!res.ok) return [] as ServerPlace[];
    const data = await res.json().catch(() => null);
    const arr = (data && data.ok !== false && Array.isArray(data.results)) ? (data.results as ServerPlace[]) : [];
    setCached(key, arr);
    return arr;
  })();
  inflight.set(key, p);
  try { return await p; } finally { inflight.delete(key); }
}

export type ServerPlaceDetails = {
  place_id: string;
  name: string;
  formatted_address: string;
  international_phone_number?: string;
  website?: string;
  url?: string;
  geometry?: { lat: number; lng: number };
  types?: string[];
  rating?: number;
  user_ratings_total?: number;
  opening_hours?: string[] | null;
  open_now?: boolean | null;
};

export async function getPlaceDetailsServer(placeId: string, opts?: { signal?: AbortSignal }) {
  if (!placeId) return null as ServerPlaceDetails | null;
  const base = getBase();
  const origin = (typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
  const u = new URL(`${base}/places/details`, origin);
  u.searchParams.set('id', placeId);
  const key = JSON.stringify({ mode: 'details', id: placeId });
  const cached = getCached<ServerPlaceDetails | null>(key, DETAILS_TTL_MS);
  if (cached) return cached;
  if (inflight.has(key)) {
    try { return await inflight.get(key)! as ServerPlaceDetails | null; } finally {}
  }
  const p = (async () => {
    const res = await fetch(u.toString(), { signal: opts?.signal, credentials: 'omit' });
    if (!res.ok) return null;
    const data = await res.json().catch(() => null);
    const out = (data && data.ok !== false) ? ((data.result || null) as ServerPlaceDetails | null) : null;
    setCached(key, out);
    return out;
  })();
  inflight.set(key, p);
  try { return await p; } finally { inflight.delete(key); }
}
